import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Search, X, MessageCircle, PackageSearch } from "lucide-react";
import { waLink } from "../lib/site";
import { API, imgUrl } from "../lib/api";
import { useContent } from "../context/ContentContext";

const LineBadge = ({ meta }) => (
  <span
    className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
    style={{ background: meta.badgeBg, border: `1px solid ${meta.badgeBorder}`, color: meta.color }}
  >
    {meta.name}
  </span>
);

export default function Catalog({ lineFilter, setLineFilter }) {
  const content = useContent();
  const { lines, getLine } = content;
  const intro = content.catalog_intro;
  const wa = (msg) => waLink(msg, content.whatsapp_number);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [format, setFormat] = useState("todos");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [selected]);

  const formats = useMemo(
    () => ["todos", ...new Set(products.map((p) => p.format))],
    [products]
  );

  const filtered = products.filter((p) => {
    const q = search.trim().toLowerCase();
    const matchSearch =
      !q ||
      [p.name, p.active_ingredient, p.description].join(" ").toLowerCase().includes(q);
    return (
      matchSearch &&
      (lineFilter === "todas" || p.line === lineFilter) &&
      (format === "todos" || p.format === format)
    );
  });

  return (
    <section id="catalogo" className="border-y border-[#E8E4D9] bg-ivory py-24 lg:py-32" data-testid="catalog-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="font-mono mb-4 text-xs uppercase tracking-[0.3em] text-brand">
            {intro.overline}
          </p>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-neutral-900 sm:text-3xl lg:text-4xl">
            {intro.title}
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-500 sm:text-base">
            {intro.subtitle}
          </p>
        </motion.div>

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, principio activo..."
              data-testid="catalog-search-input"
              className="w-full rounded-full border border-[#DDD8C8] bg-white py-3 pl-11 pr-4 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="line-filters">
            {[{ id: "todas", name: "Todas" }, ...lines].map((l) => (
              <button
                key={l.id}
                onClick={() => setLineFilter(l.id)}
                data-testid={`line-filter-${l.id}`}
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                  lineFilter === l.id
                    ? "border-brand bg-brand text-white"
                    : "border-[#DDD8C8] bg-white text-neutral-600 hover:border-brand/50"
                }`}
              >
                {l.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-10 flex flex-wrap gap-2" data-testid="format-filters">
          {formats.map((f) => (
            <button
              key={f}
              onClick={() => setFormat(f)}
              data-testid={`format-filter-${f}`}
              className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-all duration-200 ${
                format === f
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-400 hover:text-neutral-700"
              }`}
            >
              {f === "todos" ? "Todos los formatos" : f}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-20 text-center font-mono text-xs uppercase tracking-widest text-neutral-400" data-testid="catalog-loading">
            Cargando catálogo…
          </p>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center" data-testid="catalog-empty">
            <PackageSearch size={40} className="mb-4 text-neutral-300" />
            <p className="text-sm text-neutral-500">
              No encontramos productos para tu búsqueda. Escríbenos por WhatsApp y te ayudamos.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="catalog-grid">
            {filtered.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.6 }}
                whileHover={{ scale: 1.02 }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-[#E8E4D9] bg-white shadow-sm transition-all duration-300 hover:border-brand/50 hover:shadow-xl"
                data-testid={`product-card-${p.id}`}
              >
                <button
                  onClick={() => setSelected(p)}
                  className="relative block aspect-[4/3] w-full overflow-hidden bg-white"
                  data-testid={`product-detail-open-${p.id}`}
                >
                  <img
                    src={imgUrl(p.image)}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-contain p-4 transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute left-3 top-3">
                    <LineBadge meta={getLine(p.line)} />
                  </div>
                </button>
                <div className="flex flex-1 flex-col border-t border-neutral-100 p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                    {p.active_ingredient}
                  </p>
                  <h3 className="font-display mt-1.5 text-base font-bold leading-snug text-neutral-900">{p.name}</h3>
                  <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
                    <span className="rounded bg-brand-light px-2 py-0.5 font-semibold text-brand-dark">{p.concentration}</span>
                    <span className="capitalize">{p.format}</span>
                  </div>
                  <div className="mt-4 flex flex-1 items-end gap-2">
                    <a
                      href={wa(`Hola Landerlan Dominicana, deseo solicitar información y pedido de: ${p.name} (${p.concentration}).`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`product-whatsapp-${p.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-wa px-3 py-2.5 text-xs font-bold text-black transition-colors duration-200 hover:bg-wa-hover"
                    >
                      <MessageCircle size={14} strokeWidth={2.5} />
                      Pedir
                    </a>
                    <button
                      onClick={() => setSelected(p)}
                      data-testid={`product-info-${p.id}`}
                      className="rounded-full border border-neutral-200 px-4 py-2.5 text-xs font-bold text-neutral-700 transition-colors duration-200 hover:border-brand hover:text-brand"
                    >
                      Detalle
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setSelected(null)}
            data-testid="product-modal-backdrop"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl border border-[#E8E4D9] bg-white sm:rounded-3xl"
              data-testid="product-modal"
            >
              <div className="relative flex h-56 items-center justify-center bg-ivory sm:h-72">
                <img src={imgUrl(selected.image)} alt={selected.name} className="h-full w-full object-contain p-6" />
                <button
                  onClick={() => setSelected(null)}
                  data-testid="product-modal-close"
                  className="absolute right-4 top-4 rounded-full bg-white p-2 text-neutral-700 shadow-md transition-colors hover:bg-neutral-100"
                  aria-label="Cerrar detalle"
                >
                  <X size={18} />
                </button>
                <div className="absolute bottom-4 left-6">
                  <LineBadge meta={getLine(selected.line)} />
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">
                  {selected.active_ingredient}
                </p>
                <h3 className="font-display mt-2 text-2xl font-bold text-neutral-900 sm:text-3xl" data-testid="product-modal-title">
                  {selected.name}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-neutral-600">{selected.description}</p>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-[#E8E4D9] bg-ivory p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Concentración</p>
                    <p className="mt-1 text-sm font-bold text-neutral-900">{selected.concentration}</p>
                  </div>
                  <div className="rounded-xl border border-[#E8E4D9] bg-ivory p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Formato</p>
                    <p className="mt-1 text-sm font-bold capitalize text-neutral-900">{selected.format}</p>
                  </div>
                  <div className="col-span-2 rounded-xl border border-[#E8E4D9] bg-ivory p-3 sm:col-span-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">Presentación</p>
                    <p className="mt-1 text-sm font-bold text-neutral-900">{selected.presentation}</p>
                  </div>
                </div>
                <p className="mt-5 text-[11px] leading-relaxed text-neutral-400">
                  Producto sujeto a prescripción y supervisión de un profesional de la salud.
                  Verifique siempre el código QR de autenticidad antes de usar.
                </p>
                <a
                  href={wa(`Hola Landerlan Dominicana, deseo solicitar información y pedido de: ${selected.name} (${selected.concentration}, ${selected.presentation}).`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-modal-whatsapp"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-wa px-6 py-4 text-sm font-bold text-black transition-colors duration-200 hover:bg-wa-hover"
                >
                  <MessageCircle size={18} strokeWidth={2.5} />
                  Pedir este producto por WhatsApp
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
